import { useEffect, useRef, useState, type ReactNode } from "react";

import LineIcon from "@/components/marketing/sections/LineIcon";
import { STEM_ICONS } from "@/components/marketing/stems/stemIcons";
import { cn } from "@/lib/utils";

type DemoTrack = { label: string; src: string; icon: ReactNode };

const TRACKS: DemoTrack[] = [
  {
    label: "Original",
    src: "/marketing/stems/original.mp3",
    icon: (
      <>
        <circle cx="12" cy="12" r="9" fill="currentColor" stroke="none" />
        <path d="m10 8 6 4-6 4z" fill="#fff" stroke="none" />
      </>
    ),
  },
  {
    label: "Instrumental",
    src: "/marketing/stems/instrumental.mp3",
    icon: STEM_ICONS.instrumental,
  },
  {
    label: "Vocals",
    src: "/marketing/stems/vocals.mp3",
    icon: STEM_ICONS.vocals,
  },
  { label: "Drums", src: "/marketing/stems/drums.mp3", icon: STEM_ICONS.drums },
  { label: "Bass", src: "/marketing/stems/bass.mp3", icon: STEM_ICONS.bass },
];

function formatTime(seconds: number) {
  const whole = Math.floor(seconds);
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, "0")}`;
}

/** Hero player that switches between the stems of one real Lutely track. */
export default function StemsDemo() {
  const audioRef = useRef<HTMLAudioElement>(null);
  const resumeRef = useRef<{ time: number; play: boolean } | null>(null);
  const [active, setActive] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const track = TRACKS[active];

  useEffect(() => {
    const audio = audioRef.current;
    return () => audio?.pause();
  }, []);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) audio.play().catch(() => {});
    else audio.pause();
  };

  const selectTrack = (index: number) => {
    const audio = audioRef.current;
    if (!audio || index === active) return;
    resumeRef.current ??= { time: audio.currentTime, play: !audio.paused };
    audio.pause();
    setActive(index);
  };

  const handleLoadedMetadata = () => {
    const audio = audioRef.current;
    if (!audio) return;
    setDuration(audio.duration);
    const resume = resumeRef.current;
    resumeRef.current = null;
    if (!resume) return;
    audio.currentTime = Math.min(
      resume.time,
      Math.max(0, audio.duration - 0.1),
    );
    setTime(audio.currentTime);
    if (resume.play) audio.play().catch(() => {});
  };

  const seek = (value: number) => {
    const audio = audioRef.current;
    if (!audio || !duration) return;
    audio.currentTime = value;
    setTime(value);
  };

  return (
    <div
      role="group"
      aria-label="Interactive stem separation demo"
      className="relative z-[1] w-[min(610px,100%)] rounded-[26px] border border-white/[0.74] bg-white p-6 text-marketing-ink shadow-[0_30px_90px_rgba(0,0,0,0.38)] max-[680px]:rounded-[20px] max-[680px]:p-[18px]"
    >
      <a
        href="https://lutely.com/@paulsheldrick/paul-sheldrick-epii-2021"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Listen to Paul Sheldrick EPII (2021) on Lutely"
        className="flex items-center gap-[18px]"
      >
        <img
          src="/marketing/stems/paul-sheldrick-epii.webp"
          alt="Paul Sheldrick EPII (2021) cover artwork"
          className="size-[104px] shrink-0 rounded-[15px] object-cover max-[680px]:size-[86px]"
        />
        <div className="min-w-0">
          <span className="mb-[5px] block font-figtree text-[12px] font-[850] uppercase leading-none tracking-[0.08em] text-blue-600">
            Real Lutely stem demo
          </span>
          <strong className="block font-figtree text-[25px] font-[850] leading-[1.1] max-[680px]:text-[22px]">
            6 Feet Down
          </strong>
          <div className="mt-[11px] flex items-center gap-[9px] font-figtree">
            <img
              src="https://d19tdzlvcbcfe2.cloudfront.net/creators/166980df-4457-4d57-9394-c90643fabf5d/profile/image.jpg"
              alt="Paul Sheldrick"
              className="size-[34px] shrink-0 rounded-full object-cover max-[680px]:size-[30px]"
            />
            <div>
              <b className="block text-[14px] leading-[1.15] text-marketing-ink">
                Paul Sheldrick
              </b>
              <small className="mt-[2px] block text-[11px] leading-[1.2] text-[#727A8D]">
                Paul Sheldrick EPII (2021)
              </small>
            </div>
          </div>
        </div>
      </a>

      <div className="mt-5 grid grid-cols-[48px_minmax(0,1fr)_42px] items-center gap-3 font-open-sans max-[680px]:grid-cols-[42px_minmax(0,1fr)_38px] max-[680px]:gap-2">
        <button
          type="button"
          onClick={togglePlay}
          aria-label={`${playing ? "Pause" : "Play"} ${track.label}`}
          className={cn(
            "size-12 cursor-pointer rounded-full border-0 text-[16px] max-[680px]:size-[42px]",
            playing
              ? "bg-teal-500 text-marketing-navy"
              : "bg-marketing-navy text-white",
          )}
        >
          <span aria-hidden>{playing ? "Ⅱ" : "▶"}</span>
        </button>
        <div className="relative h-[54px] overflow-hidden has-[:focus-visible]:outline has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-blue-600">
          <span
            aria-hidden
            className="absolute inset-0 opacity-[0.48] [background:repeating-linear-gradient(90deg,#8D94A5_0_2px,transparent_2px_7px)] [mask-image:linear-gradient(180deg,transparent_4%,#000_24%,#000_76%,transparent_96%)]"
          />
          <span
            aria-hidden
            style={{ width: `${duration ? (time / duration) * 100 : 0}%` }}
            className="absolute inset-y-0 left-0 [background:repeating-linear-gradient(90deg,#3042E2_0_2px,transparent_2px_7px)] [mask-image:linear-gradient(180deg,transparent_4%,#000_24%,#000_76%,transparent_96%)]"
          />
          <input
            type="range"
            min={0}
            max={duration || 0}
            step={1}
            value={time}
            disabled={!duration}
            onChange={(e) => seek(Number(e.target.value))}
            aria-label="Seek audio"
            aria-valuetext={`${formatTime(time)} of ${formatTime(duration)}`}
            className="absolute inset-0 m-0 size-full cursor-pointer appearance-none bg-transparent opacity-0 disabled:cursor-default"
          />
        </div>
        <time
          dateTime={`PT${Math.floor(time)}S`}
          className="text-[13px] font-[750] text-[#727A8D]"
        >
          {formatTime(time)}
        </time>
      </div>

      <div className="mt-[22px] grid grid-cols-5 gap-2 max-[950px]:grid-cols-3">
        {TRACKS.map((item, i) => (
          <button
            key={item.label}
            type="button"
            aria-pressed={i === active}
            onClick={() => selectTrack(i)}
            className={cn(
              "flex min-w-0 cursor-pointer flex-col items-center gap-[7px] rounded-[13px] border px-1 pb-[9px] pt-[11px] transition-[border-color,color,background,transform] duration-[180ms] ease-[ease] motion-safe:hover:-translate-y-px",
              i === active
                ? "border-blue-600 bg-[#EEF1FF] text-blue-600"
                : "border-[#DFE3ED] bg-white text-[#5E6471] hover:border-[#AEB7CB]",
            )}
          >
            <LineIcon className="block size-[25px]">{item.icon}</LineIcon>
            <span className="max-w-full overflow-hidden text-ellipsis font-figtree text-[10px] font-extrabold leading-[1.1] max-[950px]:text-[12px]">
              {item.label}
            </span>
          </button>
        ))}
      </div>

      <audio
        ref={audioRef}
        src={track.src}
        preload="metadata"
        onLoadedMetadata={handleLoadedMetadata}
        onTimeUpdate={(e) => {
          if (!resumeRef.current) setTime(e.currentTarget.currentTime);
        }}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
      />
    </div>
  );
}
